import { useState, useEffect } from "react";
import "./SalesReport.css";
import { CreditCard, Download, Gift, Receipt, ShoppingBag, Wallet } from "lucide-react";
import {
    Badge,
    Button,
    Card,
    EmptyState,
    Input,
    PageHeader,
    Pagination,
    SearchInput,
    Select,
    StatCard,
    TableSkeleton,
} from "../../Components/ui";
import "./SalesPage.css";
import api from "../../Components/RefreshToken/RefreshToken";
import { MAIN_URL } from "../../constants";

// Hozircha ishlamaydigan boshqaruv elementlari (handler'i yo'q) yashirilgan.
// Batafsil: docs/known-issues.md. Mantiq ulanganda tegishli flag true qilinadi.
const SALES_FEATURES = { search: false, dateFilter: false, excelExport: false };

// Ma'lumot hali kelmagan bo'lsa (undefined/null) karta skeleton holatida ko'rinadi.
const isMissing = (value) => value === undefined || value === null;

const SalesReport = () => {
    const [salesNumber, setSalesNumber] = useState(null);
    const [orders, setOrders] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState("");

    const limit = 10;
    const TOKEN = localStorage.getItem("admin_token");

    /* ================= SALES STATS ================= */
    useEffect(() => {
        api.get(`${MAIN_URL}/management/sales/report`, {
            headers: {
                "Content-type": "application/json",
                Authorization: `Bearer ${TOKEN}`,
            },
        })
            .then((res) => setSalesNumber(res.data))
            .catch((err) => console.log(err));
    }, []);

    /* ================= COURSES ================= */
    useEffect(() => {
        api.get("/courses", {
            headers: {
                accept: "application/json",
                Authorization: `Bearer ${TOKEN}`,
            },
        })
            .then((res) => setCourses(res?.data || []))
            .catch((err) => console.log(err));
    }, []);

    /* ================= ORDERS ================= */
    useEffect(() => {
        fetchOrders();
    }, [currentPage]);

    const fetchOrders = () => {
        setLoading(true);
        const skip = (currentPage - 1) * limit;

        api.get(
            `${MAIN_URL}/management/sales/orders?skip=${skip}&limit=${limit}`,
            {
                headers: {
                    "Content-type": "application/json",
                    Authorization: `Bearer ${TOKEN}`,
                },
            }
        )
            .then((res) => {
                setOrders(res.data.result || []);
                setTotalCount(res.data.total_count || 0);
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    };

    /* ================= FRONTEND COURSE FILTER ================= */
    const filteredOrders = selectedCourse
        ? orders.filter(
              (order) => order.course?.title === selectedCourse
          )
        : orders;

    const totalPages = Math.ceil(totalCount / limit);

    const formatDate = (date) =>
        new Date(date).toLocaleDateString("uz-UZ");

    const formatAmount = (amount) =>
        new Intl.NumberFormat("uz-UZ").format(amount);

    return (
        <div className="ui-sales">
            <PageHeader
                title="Sotuvlar"
                subtitle="Barcha moliyaviy tranzaksiyalar"
                actions={
                    SALES_FEATURES.excelExport && (
                        <Button variant="secondary" leftIcon={<Download />}>
                            Excelga yuklash
                        </Button>
                    )
                }
            />

            {/* STATS */}
            <div className="ui-sales__stats">
                <StatCard
                    icon={<ShoppingBag />}
                    label="Jami sotuvlar"
                    value={isMissing(salesNumber?.total_orders) ? null : formatAmount(salesNumber.total_orders)}
                    tone="info"
                    loading={isMissing(salesNumber?.total_orders)}
                />
                <StatCard
                    icon={<CreditCard />}
                    label="Pullik"
                    value={isMissing(salesNumber?.paid_orders) ? null : formatAmount(salesNumber.paid_orders)}
                    tone="success"
                    loading={isMissing(salesNumber?.paid_orders)}
                />
                <StatCard
                    icon={<Gift />}
                    label="Tekin"
                    value={isMissing(salesNumber?.promo_orders) ? null : formatAmount(salesNumber.promo_orders)}
                    tone="warning"
                    loading={isMissing(salesNumber?.promo_orders)}
                />
                <StatCard
                    icon={<Wallet />}
                    label="Jami tushum"
                    value={isMissing(salesNumber?.total_revenue) ? null : `${formatAmount(salesNumber.total_revenue)} UZS`}
                    tone="brand"
                    loading={isMissing(salesNumber?.total_revenue)}
                />
            </div>

            {/* FILTERS */}
            <Card padding="sm">
                <div className="ui-sales__filters">
                    {SALES_FEATURES.search && (
                        <SearchInput placeholder="Qidirish..." wrapperClassName="ui-sales__filter ui-sales__filter--search" />
                    )}

                    {/* COURSE FILTER */}
                    <Select
                        aria-label="Kurs"
                        wrapperClassName="ui-sales__filter"
                        value={selectedCourse}
                        onChange={(e) => {
                            setCurrentPage(1);
                            setSelectedCourse(e.target.value);
                        }}
                    >
                        <option value="">Barcha kurslar</option>
                        {courses.map((course) => (
                            <option key={course.id} value={course.title}>
                                {course.title}
                            </option>
                        ))}
                    </Select>

                    {SALES_FEATURES.dateFilter && (
                        <Input type="date" aria-label="Sana" wrapperClassName="ui-sales__filter ui-sales__filter--date" />
                    )}
                </div>
            </Card>

            {/* TABLE */}
            {loading ? (
                <TableSkeleton rows={limit} cols={6} />
            ) : filteredOrders.length > 0 ? (
                <div className="ui-table-wrap">
                    <table className="ui-table ui-sales__table">
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>Kurs</th>
                                <th>Turi</th>
                                <th>Promokod</th>
                                <th className="ui-table-cell--numeric">Summa</th>
                                <th>Sana</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((order) => (
                                <tr key={order.id}>
                                    <td>
                                        <div className="ui-sales__student">
                                            <span className="ui-sales__student-name">
                                                {order.user.first_name}{" "}
                                                {order.user.last_name}
                                            </span>
                                            <span className="ui-sales__student-phone">
                                                {order.user.phone_number}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="ui-sales__course" title={order.course.title}>
                                            {order.course.title}
                                        </span>
                                    </td>
                                    <td>
                                        {order.paid_amount > 0 ? (
                                            <Badge variant="success" size="sm">Pullik</Badge>
                                        ) : (
                                            <Badge variant="info" size="sm">Tekin</Badge>
                                        )}
                                    </td>
                                    <td>
                                        {order.promo_code ? (
                                            <span className="ui-sales__promo">{order.promo_code}</span>
                                        ) : (
                                            <span className="ui-sales__muted">—</span>
                                        )}
                                    </td>
                                    <td className="ui-table-cell--numeric">
                                        {formatAmount(order.paid_amount)} UZS
                                    </td>
                                    <td className="ui-sales__date">{formatDate(order.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <Card padding="none">
                    <EmptyState
                        icon={<Receipt />}
                        title="Tranzaksiyalar topilmadi"
                        description="Filtrlarni o'zgartirib ko'ring"
                    />
                </Card>
            )}

            {/* PAGINATION */}
            <div className="ui-sales__footer">
                <span className="ui-sales__total">Jami: {formatAmount(totalCount)} ta</span>
                <Pagination
                    page={currentPage}
                    totalPages={totalPages || 1}
                    onPageChange={setCurrentPage}
                    disabled={loading}
                />
            </div>
        </div>
    );
};

export default SalesReport;
